import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AppCategory } from "../AppsSidebar/types";

/**
 * El título del menú de aplicaciones, que se convierte en buscador.
 *
 * ── Por qué se transforma en vez de sumarse ─────────────────────────────────
 * Un campo de búsqueda permanente ocupa una fila entera de las cinco que tiene
 * el panel, para algo que con nueve aplicaciones casi nunca hace falta. Y un
 * panel que se abre con una caja de texto vacía pide escribir antes de dejar
 * mirar.
 *
 * Así que el buscador ocupa EL MISMO sitio que el título y entra deslizándose
 * sobre él. Cerrado, no cuesta un píxel; abierto, no añade ninguno. El icono de
 * lupa es lo único que se ve, y eso ya dice que se puede buscar.
 *
 * Los filtros por sección aparecen sólo con el buscador abierto: mientras no
 * buscas, los rótulos de categoría de la rejilla ya separan los grupos, y
 * repetirlos arriba sería decir dos veces lo mismo.
 */

interface Props {
  abierto: boolean;
  onAbrir: () => void;
  onCerrar: () => void;
  texto: string;
  onTexto: (t: string) => void;
  categorias: AppCategory[];
  seccion: string | null;
  onSeccion: (id: string | null) => void;
  resultados: number;
}

export default function CabeceraDeApps({
  abierto,
  onAbrir,
  onCerrar,
  texto,
  onTexto,
  categorias,
  seccion,
  onSeccion,
  resultados,
}: Props) {
  const { t } = useTranslation("common");
  const refInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (abierto) refInput.current?.focus();
  }, [abierto]);

  return (
    <div className="shrink-0">
      <div className="relative flex h-[46px] items-center px-3">
        {/*
          Título y buscador ocupan la MISMA fila, superpuestos. El que no está
          activo se va a un lado y deja de recibir el cursor, para que no se
          pueda escribir en un campo invisible.
        */}
        <h2
          className={`hud-display absolute left-4 truncate transition-[opacity,transform] duration-150 ${
            abierto ? "pointer-events-none -translate-x-2 opacity-0" : "opacity-100"
          }`}
        >
          {t("appsSidebar.title")}
        </h2>

        {/*
          Termina ANTES del botón de la derecha. Con `inset-x-3` esta capa se
          extendía por encima de él: el clic y el hover se los comía ella, así
          que la equis ni se iluminaba ni cerraba el buscador. Un elemento
          absoluto transparente sigue recibiendo el puntero.
        */}
        <div
          className={`absolute left-3 right-12 flex items-center gap-2 transition-[opacity,transform] duration-150 ${
            abierto ? "opacity-100" : "pointer-events-none translate-x-2 opacity-0"
          }`}
        >
          <i className="ri-search-line shrink-0 text-[15px] text-content-faint" aria-hidden="true" />
          <input
            ref={refInput}
            type="text"
            value={texto}
            onChange={(e) => onTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") return;
              /*
                Escape vacía antes de cerrar, y no deja que el panel entero se
                entere: si no, el primer Escape cerraría el menú y habría que
                volver a abrirlo para corregir una letra.
              */
              e.stopPropagation();
              if (texto) onTexto("");
              else onCerrar();
            }}
            placeholder={t("appsSidebar.searchPlaceholder", "Buscar aplicación…")}
            aria-label={t("appsSidebar.searchPlaceholder", "Buscar aplicación…")}
            className="h-9 min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-content placeholder:text-content-faint focus:outline-none focus:ring-0"
          />
          {texto && (
            <>
              <span className="hud-dato shrink-0 text-[10px] text-content-faint">
                {resultados}
              </span>
              {/*
                Borrar lo escrito y cerrar el buscador son dos cosas distintas y
                por eso son dos botones. Con uno solo que cambiara de sentido
                según hubiera texto, nadie sabría cuál de las dos va a pasar
                antes de pulsarlo.
              */}
              <button
                type="button"
                onClick={() => {
                  onTexto("");
                  refInput.current?.focus();
                }}
                aria-label={t("appsSidebar.clearSearch", "Borrar lo escrito")}
                title={t("appsSidebar.clearSearch", "Borrar lo escrito")}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-content-faint transition-colors hover:bg-subtle hover:text-content-secondary"
              >
                <i className="ri-close-circle-fill text-[14px]" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={abierto ? onCerrar : onAbrir}
          aria-label={
            abierto
              ? t("appsSidebar.closeSearch", "Cerrar la búsqueda")
              : t("appsSidebar.search", "Buscar aplicación")
          }
          aria-expanded={abierto}
          className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-content-faint transition-colors hover:bg-subtle hover:text-content-secondary"
        >
          <i
            className={abierto ? "ri-close-line text-[16px]" : "ri-search-line text-[16px]"}
            aria-hidden="true"
          />
        </button>
      </div>

      {/*
        Las secciones, sólo mientras se busca. En una sola fila que se desplaza:
        apilarlas en varias líneas movería la rejilla hacia abajo cada vez que
        se abre el buscador.
      */}
      {abierto && categorias.length > 1 && (
        <TiraDeSecciones>
          <ChipDeSeccion
            activo={seccion === null}
            onClick={() => onSeccion(null)}
            texto={t("filters.all", "Todas")}
          />
          {categorias.map((c) => (
            <ChipDeSeccion
              key={c.id}
              activo={seccion === c.id}
              onClick={() => onSeccion(seccion === c.id ? null : c.id)}
              texto={c.title}
            />
          ))}
        </TiraDeSecciones>
      )}
    </div>
  );
}

function ChipDeSeccion({
  activo,
  onClick,
  texto,
}: {
  activo: boolean;
  onClick: () => void;
  texto: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={`shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-[11px] transition-colors ${
        activo
          ? "bg-content text-app"
          : "bg-subtle text-content-muted hover:text-content-secondary"
      }`}
    >
      {texto}
    </button>
  );
}

/**
 * La fila de secciones: una sola línea que se desplaza.
 *
 * Apilarlas en varias líneas empujaría la rejilla hacia abajo cada vez que se
 * abre el buscador, y con muchas secciones el menú entero daría un salto. Pero
 * una fila que se desborda sin nada que lo indique deja secciones inalcanzables
 * —que es como estaba—, así que lleva tres cosas:
 *
 *   • una barra de 3 px (`.tira-scroll`), para arrastrarla con el ratón;
 *   • la rueda vertical convertida en desplazamiento horizontal, porque sobre
 *     una fila el navegador no hace nada con la rueda normal y casi nadie
 *     conoce el atajo de Shift;
 *   • un desvanecido en el borde que tiene más contenido, que es lo que dice
 *     que hay más sin tener que probar.
 */
function TiraDeSecciones({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [bordes, setBordes] = useState({ izq: false, der: false });

  const medir = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const resto = el.scrollWidth - el.clientWidth - el.scrollLeft;
    setBordes({ izq: el.scrollLeft > 2, der: resto > 2 });
  }, []);

  useEffect(() => {
    medir();
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, [medir]);

  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={medir}
        onWheel={(e) => {
          const el = ref.current;
          if (!el || el.scrollWidth <= el.clientWidth) return;
          // Sólo si el gesto es vertical: con un ratón de rueda horizontal o
          // un trackpad, el navegador ya hace lo correcto y no hay que
          // duplicarlo.
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
          el.scrollLeft += e.deltaY;
        }}
        className="tira-scroll flex gap-1 overflow-x-auto px-3 pb-2"
      >
        {children}
      </div>
      {(["izq", "der"] as const).map((lado) => (
        <span
          key={lado}
          aria-hidden="true"
          className={`pointer-events-none absolute inset-y-0 w-6 transition-opacity duration-150 ${
            lado === "izq"
              ? "left-0 bg-gradient-to-r from-card to-transparent"
              : "right-0 bg-gradient-to-l from-card to-transparent"
          } ${bordes[lado] ? "opacity-100" : "opacity-0"}`}
        />
      ))}
    </div>
  );
}
